/**
 * CharacterFieldOrganizer.js
 * This module organizes the character selection panel by field categories.
 */

export class CharacterFieldOrganizer {
    constructor() {
        console.log("CharacterFieldOrganizer: Initializing...");
        this.categoriesData = null;
        this.characterData = null;

        // Will be populated during initialize
        this.characterPanel = null;
        this.characterCarousel = null;
        this.characterItems = [];
        this.originalCharacterItems = [];

        // Listen for window resize to adjust layout
        window.addEventListener("resize", () => this.refreshLayout());
    }

    /**
     * Retrieves and stores DOM elements for character organization
     * This should be called after DOM is fully loaded
     */
    setupDomReferences() {
        console.log("CharacterFieldOrganizer: Setting up DOM references...");

        // Get panel and carousel references
        this.characterPanel = document.getElementById("character-panel");
        this.characterCarousel = document.querySelector(".character-carousel");

        if (!this.characterCarousel) {
            console.error(
                "CharacterFieldOrganizer: Character carousel element not found!",
            );
            return false;
        }

        console.log(
            "CharacterFieldOrganizer: Found character carousel:",
            this.characterCarousel,
        );

        // Get all character items
        this.characterItems = Array.from(
            document.querySelectorAll(".character-item"),
        );
        console.log(
            `CharacterFieldOrganizer: Found ${this.characterItems.length} character items`,
        );

        // Store the original character items for fallback/reference
        this.originalCharacterItems = this.characterItems.map((item) => {
            return {
                modelId: item.dataset.modelId,
                element: item.cloneNode(true),
            };
        });

        // Add event listener for panel toggle to ensure content is properly sized
        const panelHeader = document.querySelector(".panel-header");
        if (panelHeader) {
            panelHeader.addEventListener("click", () => {
                // Allow time for panel animation to complete before refreshing
                setTimeout(() => this.refreshLayout(), 350);
            });
        }

        return true;
    }

    /**
     * Initializes the organizer by fetching categories data
     */
    async initialize() {
        try {
            console.log("CharacterFieldOrganizer: Starting initialization...");

            // First, set up DOM references to ensure we have all required elements
            if (!this.setupDomReferences()) {
                console.error(
                    "CharacterFieldOrganizer: Failed to set up DOM references, aborting initialization",
                );
                return false;
            }

            // Load both data sources in parallel
            console.log("CharacterFieldOrganizer: Loading data sources...");
            const [categoriesResponse, characterDataResponse] =
                await Promise.all([
                    fetch("/static/data/characters.json"),
                    this.importCharacterData(),
                ]);

            if (!categoriesResponse.ok) {
                throw new Error(
                    `Failed to fetch categories data: ${categoriesResponse.status}`,
                );
            }

            this.categoriesData = await categoriesResponse.json();

            console.log(
                "CharacterFieldOrganizer: Character field data loaded successfully!",
            );
            console.log(
                "CharacterFieldOrganizer: Categories loaded:",
                this.categoriesData.fields.map((f) => f.name).join(", "),
            );

            // Organize characters by field
            console.log(
                "CharacterFieldOrganizer: Now organizing characters by field...",
            );
            this.organizeCharactersByField();

            // Refresh layout after a brief delay to ensure rendering completes
            setTimeout(() => this.refreshLayout(), 500);

            return true;
        } catch (error) {
            console.error(
                "CharacterFieldOrganizer: Error during initialization:",
                error,
            );
            return false;
        }
    }

    /**
     * Import the character data module
     * @returns {Promise<Object>} The character data
     */
    async importCharacterData() {
        try {
            const module = await import("./characterData.js");
            this.characterData = module.characterData;
            return this.characterData;
        } catch (error) {
            console.error("Error importing character data:", error);
            return null;
        }
    }

    /**
     * Refreshes the layout after panel toggle or window resize
     */
    refreshLayout() {
        // Ensure category headings and grids are properly sized
        const categoryGrids = document.querySelectorAll(".character-grid");
        if (categoryGrids.length > 0) {
            categoryGrids.forEach((grid) => {
                const availableWidth = grid.clientWidth;
                const itemWidth = 150; // Base width of character items
                const cols = Math.max(
                    1,
                    Math.floor(availableWidth / (itemWidth + 16)),
                ); // 16px is the gap

                if (window.innerWidth <= 767) {
                    // Mobile view: force 2 columns max
                    grid.style.gridTemplateColumns = `repeat(2, 1fr)`;
                } else {
                    // Desktop/tablet view: responsive columns
                    grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
                }
            });
        }
    }

    /**
     * Organizes the character panel by field categories
     * Works with existing character elements in the HTML
     */
    organizeCharactersByField() {
        try {
            console.log(
                "CharacterFieldOrganizer: Organizing existing character elements by field...",
            );

            // Validate required elements
            if (!this.characterCarousel) {
                console.error(
                    "CharacterFieldOrganizer: Character carousel not found",
                );
                return;
            }

            // Store original content for fallback
            const originalHtml = this.characterCarousel.innerHTML;

            // Get all existing character items
            const existingCharacterItems =
                this.characterCarousel.querySelectorAll(".character-item");
            console.log(
                `CharacterFieldOrganizer: Found ${existingCharacterItems.length} existing character items`,
            );

            // Character field mapping (model ID to field)
            const characterFields = {
                // Scientists & Innovators
                "bc5d31a8-047b-4f72-8f16-fea7a365ca69": "Science", // Richard Feynman
                "16f5b6a1-45f6-4c01-a612-60d4e4ab2297": "Science", // René Descartes
                "5eb1ad8d-4c11-4174-b79a-c232991d015b": "Science", // Oppenheimer
                "7021ffd6-51bb-41a8-920e-935899dcd631": "Science", // Isaac Newton
                "6da2e341-3d4e-4a06-bf8c-5cb8f499b93e": "Science", // Marie Curie
                "eca89dfa-ba70-458b-8809-57c5132e32eb": "Science", // Niels Bohr
                "693f15ff-8b86-4d06-9749-3ffd281c54df": "Science", // Albert Einstein
                "d537d61d-0313-421b-ae66-48a8d7222b3f": "Science", // Nikola Tesla

                // Leaders & Conquerors
                "f2e9f4ee-c301-4245-9e2a-5cd7a2bac6e9": "Leadership", // Genghis Khan
                "9ef81498-3fb9-4175-a6d4-c2a02d71c640": "Leadership", // Cyrus the Great
                "b576d6d5-b9c4-43af-b6d4-e39540c0e209": "Leadership", // Mansa Musa
                "5a16ff56-6e03-414e-a89d-cda9d83fae60": "Leadership", // Henry Ford
                "04a8be6f-7060-4613-bbd5-c6c4638f7460": "Leadership", // Warren Buffett

                // Philosophers & Thinkers
                "b8951237-698b-42b5-8b6e-ee381b91fb65": "Philosophy", // Confucius
                "5a42d57c-6e2b-4ba8-bd32-838a1a5c5e6b": "Philosophy", // Machiavelli
                "ce56dffd-fef6-4926-8d40-e16487c9f7dc": "Philosophy", // Plato
                "88c314c4-5890-4e55-9418-8b46a376ef81a": "Philosophy", // Friedrich Nietzsche

                // Artists & Creators
                "80b9b471-5292-4e67-aac7-82dc27b48d8b": "Arts", // Leonardo da Vinci
                "240336f6-f28c-4c70-84a2-6b89fa5b246a": "Arts", // Van Gogh
                "97a3ec85-b2e3-45d8-842a-898e58a60b2d": "Arts", // Salvador Dali
                "40bf591e-aef9-446a-8d86-3d5008a80e38": "Arts", // George Orwell

                // Sports Legends
                "c662d714-686f-421f-9c23-6ebd08ce3c55": "Sports", // Muhammad Ali

                // Economic Theorists
                "a59fe2c1-9046-405a-9a99-a7ddbaee13be": "Economics", // Adam Smith
                "4d7d2e17-b818-4144-b78b-629c688a71ac": "Economics", // Karl Marx

                // Adventurers & Explorers
                "770e2d93-a6a4-41ed-aca1-8951327885b5": "Exploration", // Amelia Earhart

                // Notorious Figures
                "ef00b395-5850-4cb9-9053-f80db1c8b543": "Crime", // Al Capone
            };

            // Define the main field categories and their display names
            const fieldCategories = [
                { id: "Science", name: "Scientists & Innovators" },
                { id: "Leadership", name: "Leaders & Conquerors" },
                { id: "Philosophy", name: "Philosophers & Thinkers" },
                { id: "Arts", name: "Artists & Creators" },
                { id: "Sports", name: "Sports Legends" },
                { id: "Economics", name: "Economic Theorists" },
                { id: "Exploration", name: "Adventurers & Explorers" },
                { id: "Crime", name: "Notorious Figures" },
            ];

            // Create a temporary container to store characters by field
            const charactersByField = {};
            fieldCategories.forEach((category) => {
                charactersByField[category.id] = [];
            });

            // Sort existing characters into field categories
            existingCharacterItems.forEach((charItem) => {
                // Get the model ID from the data attribute
                const modelId = charItem.dataset.modelId;

                if (modelId && characterFields[modelId]) {
                    const field = characterFields[modelId];
                    // Add field label to the character item if not already present
                    if (!charItem.querySelector(".character-field")) {
                        const fieldLabel = document.createElement("p");
                        fieldLabel.className = "character-field";
                        fieldLabel.textContent = field;
                        charItem.appendChild(fieldLabel);
                    }

                    // Add to the appropriate field category
                    charactersByField[field].push(charItem);
                } else {
                    // Character doesn't have a known field, add to "Other"
                    if (!charactersByField["Other"]) {
                        charactersByField["Other"] = [];
                    }
                    charactersByField["Other"].push(charItem);
                }
            });

            // Clear the carousel to rebuild with categories
            this.characterCarousel.innerHTML = "";

            // Create sections for each field and add characters
            let totalCharactersAdded = 0;

            // Add each field category
            fieldCategories.forEach((category) => {
                const charactersInField = charactersByField[category.id] || [];

                // Only create sections for fields that have characters
                if (charactersInField.length > 0) {
                    console.log(
                        `CharacterFieldOrganizer: Creating section for field "${category.name}" with ${charactersInField.length} characters`,
                    );

                    // Create category section
                    const categorySection = document.createElement("div");
                    categorySection.className = "character-category";

                    // Add heading
                    const categoryHeading = document.createElement("h3");
                    categoryHeading.className = "category-heading";
                    categoryHeading.textContent = category.name;
                    categorySection.appendChild(categoryHeading);

                    // Create grid for characters
                    const characterGrid = document.createElement("div");
                    characterGrid.className = "character-grid";

                    // Add each character to the grid
                    charactersInField.forEach((charItem) => {
                        // Clone the character item to maintain all its properties
                        const clone = charItem.cloneNode(true);
                        characterGrid.appendChild(clone);
                        totalCharactersAdded++;

                        // Add click event listener to the clone
                        this.addClickEventListener(clone);
                    });

                    // Add grid to section
                    categorySection.appendChild(characterGrid);

                    // Add section to carousel
                    this.characterCarousel.appendChild(categorySection);
                }
            });

            // Add any remaining characters in the "Other" category
            if (
                charactersByField["Other"] &&
                charactersByField["Other"].length > 0
            ) {
                const otherSection = document.createElement("div");
                otherSection.className = "character-category";

                const otherHeading = document.createElement("h3");
                otherHeading.className = "category-heading";
                otherHeading.textContent = "Other Characters";
                otherSection.appendChild(otherHeading);

                const otherGrid = document.createElement("div");
                otherGrid.className = "character-grid";

                charactersByField["Other"].forEach((charItem) => {
                    const clone = charItem.cloneNode(true);
                    otherGrid.appendChild(clone);
                    totalCharactersAdded++;

                    // Add click event listener
                    this.addClickEventListener(clone);
                });

                otherSection.appendChild(otherGrid);
                this.characterCarousel.appendChild(otherSection);
            }

            console.log(
                `CharacterFieldOrganizer: Organization complete. Added ${totalCharactersAdded} characters.`,
            );

            // If no characters were added, restore original content
            if (totalCharactersAdded === 0) {
                console.error(
                    "CharacterFieldOrganizer: No characters were added, restoring original content",
                );
                this.characterCarousel.innerHTML = originalHtml;
                return;
            }

            // Refresh the layout
            this.refreshLayout();
        } catch (error) {
            console.error(
                "CharacterFieldOrganizer: Error during organization:",
                error,
            );
            // If an error occurred, restore the original content
            if (this.characterCarousel) {
                this.characterCarousel.innerHTML = originalHtml || "";
            }
        }
    }

    /**
     * Creates a character item element
     * @param {Object} character - The character data
     * @returns {HTMLElement} - The character item element
     */
    createCharacterItem(character) {
        try {
            console.log(
                `Creating character item for "${character.name}" with ID: ${character.id}`,
            );

            // Create a new character item element
            const characterItem = document.createElement("div");
            characterItem.className = "character-item";
            characterItem.dataset.modelId = character.id;

            // Use the image path from the character data if available
            let imagePath = character.image || "";

            // If no image path provided, try to construct one
            if (!imagePath && character.name) {
                // Try different formats of the name
                imagePath = `/static/images/${character.name.replace(/\s+/g, "")}.png`;
            }

            // Create image element
            const imageElement = document.createElement("img");
            imageElement.alt = character.name;
            imageElement.className = "character-image";

            // Set image source with a fallback
            if (imagePath) {
                imageElement.src = imagePath;
                imageElement.onerror = () => {
                    console.log(
                        `Image not found at ${imagePath}, using fallback`,
                    );
                    imageElement.src = "/static/images/Test.jpg";
                };
            } else {
                imageElement.src = "/static/images/Test.jpg";
            }

            // Create name element
            const nameElement = document.createElement("p");
            nameElement.className = "character-name";
            nameElement.textContent = character.name;

            // Add tagline/subtitle if available
            if (character.tagline) {
                const taglineElement = document.createElement("p");
                taglineElement.className = "character-tagline";
                taglineElement.textContent = character.tagline;
                taglineElement.style.fontSize = "0.8rem";
                taglineElement.style.opacity = "0.8";
                taglineElement.style.margin = "2px 0";
                // Don't forget to append the element
                characterItem.appendChild(taglineElement);
            }

            // Add elements to character item
            characterItem.appendChild(imageElement);
            characterItem.appendChild(nameElement);

            // Add field info
            if (character.field) {
                const fieldElement = document.createElement("p");
                fieldElement.className = "character-field";
                fieldElement.textContent = character.field;
                characterItem.appendChild(fieldElement);
            }

            // Add click event listener
            this.addClickEventListener(characterItem);

            return characterItem;
        } catch (error) {
            console.error("Error creating character item:", error, character);
            return null;
        }
    }

    /**
     * Adds click event listener to character item for selection
     * @param {HTMLElement} characterItem - The character item element
     */
    addClickEventListener(characterItem) {
        characterItem.addEventListener("click", () => {
            const modelId = characterItem.dataset.modelId;
            const characterName =
                characterItem.querySelector(".character-name").textContent;

            // Show confirm dialog
            const confirmDialog = document.getElementById("switchConfirmModal");
            const selectedCharNameElement = document.getElementById(
                "selected-character-name",
            );

            if (confirmDialog && selectedCharNameElement) {
                selectedCharNameElement.textContent = characterName;
                const modalInstance = new bootstrap.Modal(confirmDialog);
                modalInstance.show();

                // Set the selected model ID for the confirm button to use
                document.getElementById("character-model").value = modelId;

                // Add click event to confirm button
                const confirmBtn =
                    document.getElementById("confirm-switch-btn");
                if (confirmBtn) {
                    // Remove any existing click handlers
                    const newConfirmBtn = confirmBtn.cloneNode(true);
                    confirmBtn.parentNode.replaceChild(
                        newConfirmBtn,
                        confirmBtn,
                    );

                    newConfirmBtn.addEventListener("click", () => {
                        console.log(
                            "CharacterFieldOrganizer: Confirm button clicked for model:",
                            modelId,
                            "Character name:",
                            characterName,
                        );

                        // Use the global ModelManager to update the current model
                        if (window.ModelManager) {
                            console.log(
                                "CharacterFieldOrganizer: Calling ModelManager.setCurrentModel with:",
                                modelId,
                            );
                            window.ModelManager.setCurrentModel(modelId, true);

                            // Hide the modal
                            modalInstance.hide();
                        } else if (window.modelManager) {
                            // Try lowercase version
                            console.log(
                                "CharacterFieldOrganizer: Using lowercase modelManager.setCurrentModel with:",
                                modelId,
                            );
                            window.modelManager.setCurrentModel(modelId, true);
                            modalInstance.hide();
                        } else {
                            console.error(
                                "ModelManager not available globally (tried both window.ModelManager and window.modelManager)",
                            );
                        }
                    });
                }
            }
        });
    }

    /**
     * Get the actual model ID from a character item element
     * @param {HTMLElement} element - The character item element
     * @returns {string} - The model ID
     */
    getModelIdFromElement(element) {
        // First try to get it from the data attribute
        const modelId = element.dataset.modelId;
        if (modelId) return modelId;

        // If not found, try to extract it from the onlick attribute
        const onClickAttr = element.getAttribute("onclick");
        if (onClickAttr && onClickAttr.includes("selectCharacter(")) {
            const match = onClickAttr.match(/selectCharacter\(['"](.*?)['"]\)/);
            if (match && match[1]) {
                return match[1];
            }
        }

        // Try finding it in nested elements (sometimes there's an anchor tag inside)
        const anchor = element.querySelector("a");
        if (anchor) {
            const anchorOnClick = anchor.getAttribute("onclick");
            if (anchorOnClick && anchorOnClick.includes("selectCharacter(")) {
                const match = anchorOnClick.match(
                    /selectCharacter\(['"](.*?)['"]\)/,
                );
                if (match && match[1]) {
                    return match[1];
                }
            }
        }

        return null;
    }

    /**
     * Finds a character item element by its ID
     * @param {string} id - The character ID
     * @returns {HTMLElement} - The character item element if found, null otherwise
     */
    findCharacterItemById(id) {
        for (const item of this.characterItems) {
            if (item.dataset.modelId === id) {
                return item;
            }
        }
        return null;
    }
}
